import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { MsalService } from '@azure/msal-angular';
import { AccountInfo } from '@azure/msal-browser';
import { firstValueFrom, Subject } from 'rxjs';
import { take, takeUntil } from 'rxjs/operators';
import { Router } from '@angular/router';
import { BroadcastChannelService } from '@services/broadcast-channel.service';
import { UserBehaviorService } from '@services/user-events/user-behavior.service';
import { LogoutService } from '@services/auth/logout.service';
import { AuthService } from '@services/auth/authService.service';
import { MsalBootstrapService } from '@services/auth/msal-bootstrap.service';
import { isMobileAuthContext } from '@services/auth/auth-device.util';

export interface dataStructure {
  id: number;
  username: string;
  email: string;
  picture: string;
  system: string;
}

@Component({
  selector: 'lib-login-form',
  templateUrl: './login-form.component.html',
  styleUrls: ['./login-form.component.css'],
  standalone: true
})
export class LoginFormComponent implements OnInit, OnDestroy {
  @Input() content!: any;

  username: string | undefined = '';
  email!: string | undefined;
  so: any;
  soTransform: any;
  soCapture: any;

  disableBtn: boolean = false;
  isResumingSession = true;

  destroy$ = new Subject<void>();

  constructor(
    private authService: MsalService,
    private appAuthService: AuthService,
    private msalBootstrapService: MsalBootstrapService,
    private broadcastChannelService: BroadcastChannelService,
    private userBehavior: UserBehaviorService,
    private router: Router,
    private logoutService: LogoutService
  ) {}

  async ngOnInit(): Promise<void> {
    let navigatedAway = false;

    try {
      const loggedByMsal = this.authService.instance.getActiveAccount();

      const sessionResult = await this.msalBootstrapService.completePortalSessionIfNeeded(
        this.authService,
        this.appAuthService
      );
      if (sessionResult.status === 'authenticated') {
        const account = this.authService.instance.getActiveAccount() ?? loggedByMsal;

        if (account) {
          await this.#publishSessionAndNavigate(account, sessionResult.source === 'redirect');
          navigatedAway = true;
          return;
        }
      }
    } catch {
      // The explicit Login action remains available when bootstrap cannot resume a session.
    } finally {
      if (!navigatedAway) {
        this.isResumingSession = false;
      }
    }
  }

  async login(): Promise<void> {
    if (this.disableBtn) {
      return;
    }

    this.disableBtn = true;
    this.isResumingSession = true;
    let navigatedAway = false;

    try {
      if (isMobileAuthContext()) {
        await this.msalBootstrapService.ensureReady(this.authService, this.appAuthService);
        await firstValueFrom(this.authService.loginRedirect().pipe(take(1), takeUntil(this.destroy$)));
        return;
      }

      await this.msalBootstrapService.ensureReady(this.authService, this.appAuthService);
      const response = await firstValueFrom(
        this.authService.loginPopup().pipe(take(1), takeUntil(this.destroy$))
      );

      if (!response?.account || !response.idToken) {
        return;
      }

      this.authService.instance.setActiveAccount(response.account);
      this.appAuthService.setAzureIdToken(response.idToken);

      const loginSucceeded = await this.#completePortalExchange(response.account, response.idToken);

      if (!loginSucceeded) {
        return;
      }

      await this.#publishSessionAndNavigate(response.account, true);
      navigatedAway = true;
    } catch {
      // Popup cancellation and exchange errors intentionally leave the user on /login.
    } finally {
      this.disableBtn = false;

      if (!navigatedAway) {
        this.isResumingSession = false;
      }
    }
  }

  /** Temporarily disable the button. */
  disableBtnTime() {
    this.disableBtn = true;

    setTimeout(() => {
      this.disableBtn = false;
    }, 5000);
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  async #completePortalExchange(account: AccountInfo, idToken: string): Promise<boolean> {
    this.appAuthService.setAzureIdToken(idToken);

    return firstValueFrom(
      this.appAuthService.login(account.username, idToken).pipe(take(1), takeUntil(this.destroy$))
    );
  }

  async #publishSessionAndNavigate(account: AccountInfo, notifyTabs: boolean): Promise<void> {
    this.email = account.username;
    this.username = account.name;
    this.so = window.navigator.userAgent;
    this.soTransform = this.so.split('(');
    this.soCapture = this.soTransform[1]?.split(';') ?? [];

    this.userBehavior.setUser({
      name: account.name ?? '',
      email: account.username,
      from: 'msal'
    });

    if (notifyTabs) {
      this.broadcastChannelService.notifyLoginSuccess();
    }

    this.logoutService.consumeLogoutReason();
    const returnUrl = this.logoutService.consumeReturnUrl();

    await this.router.navigateByUrl(returnUrl ?? '/home', {
      replaceUrl: true
    });
  }
}
